import { ChangeDetectionStrategy, Component, computed, inject, input, output } from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';
import { PaperButtonComponent, PaperKbdComponent, PaperTagComponent } from '@ricoh-paper/angular';
import { AppBrandIconComponent } from '../../../../../shared/components/app-brand-icon/app-brand-icon.component';
import { classifyAppDestination } from '../../../../../core/models/app-link.model';
import { LocaleService } from '../../../../../core/services/locale.service';
import type { CmdkAppItem } from '../models/cmdk.model';
import { CmdkAppProvider } from '../services/cmdk-providers/app.provider';

/**
 * The preview for an app or quick link.
 *
 * AI CONTEXT — what this pane deliberately does NOT show
 *
 * The design asks for a "Recent in {app}" list. There is no such feed.
 * `ConnectionsService.getWidgetData(provider)` returns
 * `items: {label, value, url?}[]` with a single whole-payload `lastUpdated`
 * and no per-item timestamp — it is a metric strip, not a recents list, and
 * labelling it "Recent" would be a caption the data cannot support.
 *
 * `myRecentClicks(limit)` in the platform schema DOES carry a real per-click
 * `timestamp` and is the honest source for that line. It has zero frontend
 * callers today, so it is a v2 item rather than something to approximate here.
 *
 * What is shown is everything the link itself can honestly say: where it goes,
 * what kind of destination it is, who it came from, and the admin's own
 * description when there is one.
 */
@Component({
  selector: 'im-cmdk-preview-app',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [AppBrandIconComponent, PaperButtonComponent, PaperKbdComponent, PaperTagComponent],
  templateUrl: './cmdk-preview-app.component.html',
  styleUrl: './cmdk-preview-app.component.scss',
})
export class CmdkPreviewAppComponent {
  private readonly apps = inject(CmdkAppProvider);
  private readonly transloco = inject(TranslocoService);
  private readonly locale = inject(LocaleService);

  readonly item = input.required<CmdkAppItem>();
  readonly activated = output<void>();

  protected readonly link = computed(() => this.apps.linkFor(this.item().id));

  /**
   * Where it opens. A PDF or image is rendered inside Workplace by the
   * in-app viewer; everything else leaves for a new window. Worth saying:
   * it is the difference between losing your place and not.
   */
  protected readonly destination = computed(() => {
    this.locale.locale();
    // A Workplace app opens here; Manage and every web link leave for a new window.
    if (this.item().actionId) return this.transloco.translate('shell.cmdk.app.opens_in_app');
    const type = classifyAppDestination(this.item().url);
    return this.transloco.translate(
      type === 'web' ? 'shell.cmdk.app.opens_external' : 'shell.cmdk.app.opens_in_app',
    );
  });

  /** Organisation, division, location or the viewer's own. */
  protected readonly scopeLabel = computed(() => {
    this.locale.locale();
    const link = this.link();
    if (!link) return null;
    if (link.isCustom) return this.transloco.translate('shell.cmdk.app.scope_personal');
    switch (link.scope) {
      case 'division': return this.transloco.translate('shell.cmdk.app.scope_division');
      case 'location': return this.transloco.translate('shell.cmdk.app.scope_location');
      case 'global': return this.transloco.translate('shell.cmdk.app.scope_organisation');
      default: return null;
    }
  });

  protected readonly openLabel = computed(() => {
    this.locale.locale();
    return this.transloco.translate('shell.cmdk.app.open', { name: this.item().title });
  });
}
